// REFERENCES-target name inference (Workstream C, pgsql 30.§5).
//
// `FOREIGN KEY (film_id) REFERENCES public.▮` — the FK source columns name
// the likely target table: `film_id` → `film`. The DDL slot machinery only
// knows the position is a references target; this module reads the source
// column list from the statement tail and lifts stem-matched table
// candidates to tier 1 so they beat the alphabetical schema listing.
//
// Engine-agnostic text walk — wired in pgsql today, parity candidates on
// the other engines' references-target slots.

import type { CompletionCandidate } from '@/core/engines';

// Tail = FOREIGN KEY (<cols>) REFERENCES followed by an optional partial
// (possibly schema-qualified, quoted/bracketed) target identifier.
const FK_REFERENCES_TAIL_RE =
  /\bFOREIGN\s+KEY\s*\(([^)]*)\)\s*REFERENCES\s+[\w."$[\]`]*$/i;

/**
 * Folded candidate table-name stems derived from the FK source column list
 * at the caret's REFERENCES tail. `film_id` contributes both `film_id` and
 * `film`. The singular stem is also expanded to its naive `+s` / `+es`
 * plural forms so a `<stem>_id` column matches a pluralised target table —
 * the Oracle convention (`employee_id` → `EMPLOYEES`, `product_id` →
 * `PRODUCTS`). Engines that name tables singularly (pg/mysql sakila, mssql
 * AdventureWorks) are unaffected: their tables match the singular stem and
 * the extra plural entries simply never resolve. Empty set when the caret
 * is not at such a tail.
 */
export function findFkSourceColumnStems(
  text: string,
  offset: number,
  statementStart: number,
): ReadonlySet<string> {
  const slice = text.slice(statementStart, offset);
  const m = FK_REFERENCES_TAIL_RE.exec(slice);
  const stems = new Set<string>();
  if (!m) return stems;
  for (const raw of m[1].split(',')) {
    const col = raw.trim().replace(/^["'`[]|["'`\]]$/g, '').toLowerCase();
    if (!col) continue;
    stems.add(col);
    const stem = col.replace(/_?id$/i, '');
    if (stem && stem !== col) {
      stems.add(stem);
      stems.add(`${stem}s`);
      stems.add(`${stem}es`);
    }
  }
  return stems;
}

/**
 * Lift table/view candidates whose name matches an FK source-column stem to
 * tier 1. No demotion — the schema listing keeps its tiers; the inferred
 * target just surfaces above it. No-op on an empty stem set.
 */
export function applyReferencesTargetPromotion(
  out: CompletionCandidate[],
  stems: ReadonlySet<string>,
): void {
  if (stems.size === 0) return;
  for (let i = 0; i < out.length; i += 1) {
    const c = out[i];
    if (c.kind !== 'table' && c.kind !== 'view') continue;
    const name = (c.filterText ?? c.label).toLowerCase();
    if (stems.has(name) && c.tier !== 1) {
      out[i] = { ...c, tier: 1 };
    }
  }
}
